import { useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function Login() {
  usePageTitle("Sign In - Lansdowne Youth Rugby", "Sign in to your Lansdowne Youth Rugby account to manage your players and registrations.");
  const navigate = useNavigate();
  const { login, refresh } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success("Welcome back!");
        navigate(["admin", "coach"].includes(u.role) ? "/admin" : "/");
      } else {
        await api.post("/auth/register", form);
        await refresh();
        toast.success("Account created!");
        navigate("/");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-brand-black min-h-screen text-white flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-5 py-20">
        <Card className="w-full max-w-md bg-white/[0.03] border-white/10 rounded-none p-8" data-testid="auth-card">
          <h1 className="font-display text-4xl tracking-wide text-white mb-1">{mode === "login" ? "SIGN IN" : "CREATE ACCOUNT"}</h1>
          <p className="text-white/50 text-sm mb-8">{mode === "login" ? "Access your account or the admin dashboard." : "Save your guardian info for faster registration."}</p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-white/80">Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-brand-black border-white/15 rounded-none mt-1.5 text-white" data-testid="auth-name" />
              </div>
            )}
            <div>
              <Label className="text-white/80">Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-brand-black border-white/15 rounded-none mt-1.5 text-white" data-testid="auth-email" />
            </div>
            <div>
              <Label className="text-white/80">Password</Label>
              <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-brand-black border-white/15 rounded-none mt-1.5 text-white" data-testid="auth-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-brand-red hover:bg-brand-red/90 text-white rounded-none h-12 font-bold uppercase tracking-wider" data-testid="auth-submit">
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>
          <button className="mt-6 text-sm text-white/60 hover:text-brand-gold w-full text-center" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-toggle">
            {mode === "login" ? "Need an account? Sign up" : "Already have an account? Sign in"}
          </button>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
